import {
  Popover,
  PopoverContent,
  PopoverHandler,
} from "@material-tailwind/react";
import {
  ArrowDownIcon,
  CheckCircleIcon,
  ClockIcon,
  DocumentTextIcon,
  ExclamationCircleIcon,
  RefreshIcon,
} from "@heroicons/react/outline";
import {
  HttpError,
  useDataProvider,
  useOne,
  useUpdate,
} from "@refinedev/core";
import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { _openUrlOnNewTab } from "utills/helpers";

type ExportStatus = "processing" | "error" | "success";

type ExportItem = {
  file_name: string;
  id: string;
  is_seen: boolean;
  status: ExportStatus;
};

type ExportModuleProps = {
  token?: string;
  translate: (key: string) => string;
};

const getExportList = (payload: unknown): ExportItem[] => {
  if (Array.isArray(payload)) {
    return payload as ExportItem[];
  }

  return [];
};

const ExportModule = ({ translate, token }: ExportModuleProps) => {
  const dataProvider = useDataProvider();
  const [openPopover, setOpenPopover] = useState(false);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const {
    data: exportResponse,
    isFetching: isFetchingExports,
    refetch: refetchExportList,
  } = useOne<any, HttpError>({
    resource: "exports/popup",
    id: "",
    meta: {
      resource: "data",
    },
    queryOptions: {
      enabled: Boolean(token),
      refetchOnWindowFocus: false,
      retry: false,
      staleTime: 15_000,
    } as any,
  });

  const { mutate: markSeen, isLoading: isMarkingSeen } = useUpdate<
    any,
    HttpError,
    { task_id: string[] }
  >();

  const exportList = useMemo(
    () => getExportList(exportResponse?.data),
    [exportResponse?.data]
  );

  const hasUnreadExport = useMemo(
    () =>
      exportList.some(
        (item) => item.status === "success" && item.is_seen === false
      ),
    [exportList]
  );

  useEffect(() => {
    if (openPopover && token) {
      refetchExportList();
    }
  }, [openPopover, refetchExportList, token]);

  const handleDownloadFile = async (item: ExportItem) => {
    setDownloadingId(item.id);

    try {
      const provider = dataProvider();
      const response = await provider.getOne<any>({
        resource: "exports/downloads",
        id: item.id,
        meta: {
          resource: "data",
        },
      });

      const downloadUrl = response.data;

      if (downloadUrl) {
        _openUrlOnNewTab(downloadUrl);
        refetchExportList();
      }
    } catch (error: any) {
      toast.error(error?.message ?? "Download failed");
    } finally {
      setDownloadingId(null);
    }
  };

  const handleReadAll = () => {
    const ids = exportList.map((item) => item.id);

    if (!ids.length) {
      return;
    }

    markSeen(
      {
        resource: "exports",
        id: "seen",
        values: {
          task_id: ids,
        },
        meta: {
          ignoreResourceWrapper: true,
        },
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: () => {
          toast.success(translate("mark-read-all-successfully"));
          refetchExportList();
        },
        onError: (error) => {
          toast.error(error?.message ?? "Update failed");
        },
      }
    );
  };

  const renderStatus = (item: ExportItem) => {
    if (item.status === "processing") {
      return (
        <div className="inline-flex h-8 items-center gap-x-1.5 rounded-full bg-[#F5F6F7] px-3 text-13 font-600 text-[#6B7280]">
          <ClockIcon className="size-4" />
          {translate("preparing")}
        </div>
      );
    }

    if (item.status === "error") {
      return (
        <div className="inline-flex h-8 items-center gap-x-1.5 rounded-full bg-[#FEF2F2] px-3 text-13 font-600 text-[#B91C1C]">
          <ExclamationCircleIcon className="size-4" />
          {translate("retry")}
        </div>
      );
    }

    const isDownloading = downloadingId === item.id;

    return (
      <button
        type="button"
        className="inline-flex h-9 items-center justify-center gap-x-1.5 rounded-md bg-[#011BB6] px-3 text-14 font-600 text-white transition-colors hover:bg-[#00169A] disabled:cursor-not-allowed disabled:opacity-70"
        disabled={isDownloading}
        onClick={() => handleDownloadFile(item)}
      >
        {isDownloading ? (
          <RefreshIcon className="size-4 animate-spin" />
        ) : (
          <ArrowDownIcon className="size-4" />
        )}
        {translate("download")}
      </button>
    );
  };

  return (
    <Popover placement="bottom-end" open={openPopover} handler={setOpenPopover}>
      <PopoverHandler>
        <button
          type="button"
          className="relative mr-2 flex size-10 items-center justify-center rounded-full border border-[#D7DEFF] bg-white text-[#011BB6] shadow-sm transition-colors hover:border-[#A7B4FE] hover:bg-[#F4F6FF]"
        >
          <DocumentTextIcon className="size-5" />
          {hasUnreadExport && (
            <span className="absolute right-0 top-0 size-3 rounded-full border-2 border-white bg-[#F1600D]" />
          )}
        </button>
      </PopoverHandler>

      <PopoverContent
        className="z-[100] max-h-[32rem] w-80 overflow-hidden rounded-lg border border-[#E5E7EB] p-0 shadow-xl sm:w-96 md:w-[36rem]"
        placeholder=""
        onPointerEnterCapture={undefined}
        onPointerLeaveCapture={undefined}
        onResize={() => {}}
        onResizeCapture={() => {}}
      >
        <div className="flex h-16 items-center justify-between border-b border-[#E5E7EB] px-5">
          <div>
            <div className="text-16 font-700 text-[#2B3033]">
              {translate("most-recent-report")}
            </div>
            {exportList.length > 0 && (
              <div className="text-12 font-500 text-[#868E95]">
                {translate("your-list-report")}
              </div>
            )}
          </div>

          <button
            type="button"
            className="inline-flex size-9 items-center justify-center rounded-md text-[#011BB6] transition-colors hover:bg-[#F4F6FF] disabled:cursor-not-allowed disabled:text-[#A8B3BD]"
            disabled={!exportList.length || !hasUnreadExport || isMarkingSeen}
            onClick={handleReadAll}
          >
            {isMarkingSeen ? (
              <RefreshIcon className="size-5 animate-spin" />
            ) : (
              <CheckCircleIcon className="size-5" />
            )}
          </button>
        </div>

        {isFetchingExports && !exportList.length ? (
          <div className="flex h-72 items-center justify-center">
            <RefreshIcon className="size-6 animate-spin text-[#011BB6]" />
          </div>
        ) : exportList.length > 0 ? (
          <div className="max-h-[26rem] overflow-y-auto">
            {exportList.map((item) => (
              <div
                key={item.id}
                className="flex min-h-[4.75rem] items-center justify-between gap-x-4 border-b border-[#EEF1F4] px-5 py-3 last:border-b-0"
              >
                <div className="min-w-0 flex-1">
                  <div className="truncate text-14 font-600 text-[#2B3033]">
                    {item.file_name}
                  </div>
                  {!item.is_seen && item.status === "success" && (
                    <div className="mt-1 inline-flex h-5 items-center rounded-full bg-[#FFF7ED] px-2 text-11 font-700 text-[#F1600D]">
                      {translate("new-report")}
                    </div>
                  )}
                </div>

                <div className="shrink-0">{renderStatus(item)}</div>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex h-72 flex-col items-center justify-center px-6 text-center">
            <div className="mb-4 flex size-16 items-center justify-center rounded-full bg-[#F5F6F7]">
              <DocumentTextIcon className="size-8 text-[#868E95]" />
            </div>
            <div className="text-14 font-500 text-[#868E95]">
              {translate("export-list-empty")}
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default ExportModule;
